import type { WritableComputedRef } from 'vue';
import type { CookieApp, CookieType } from '../types/cookies';
import { useStorage } from '@vueuse/core';
import { computed } from 'vue';

interface ConsentState {
	functional: boolean;
	analytics: boolean;
	targeting: boolean;
	dismissedUntil: number | null;
}

export function useCookieConsent(appId: CookieApp) {
	const ONE_YEAR_MS = 1000 * 60 * 60 * 24 * 365;
	const storageKey = `cookie-consent:${appId}`;

	const defaultConsent: ConsentState = {
		functional: true,
		analytics: false,
		targeting: false,
		dismissedUntil: null,
	};

	const consent = useStorage<ConsentState>(storageKey, { ...defaultConsent });

	const ensureConsentShape = () => {
		if (typeof consent.value !== 'object' || consent.value === null) {
			consent.value = { ...defaultConsent };
			return;
		}

		consent.value = { ...defaultConsent, ...consent.value };
	};

	ensureConsentShape();

	const byType: Record<CookieType, WritableComputedRef<boolean>> = {
		functional: computed({
			get: () => consent.value.functional,
			set: (value: boolean) => {
				ensureConsentShape();
				consent.value.functional = value;
			},
		}),
		analytics: computed({
			get: () => consent.value.analytics,
			set: (value: boolean) => {
				ensureConsentShape();
				consent.value.analytics = value;
			},
		}),
		targeting: computed({
			get: () => consent.value.targeting,
			set: (value: boolean) => {
				ensureConsentShape();
				consent.value.targeting = value;
			},
		}),
	};

	const dismissFor = (durationMs: number = ONE_YEAR_MS) => {
		consent.value.dismissedUntil = Date.now() + durationMs;
	};

	const resetDismissal = () => {
		consent.value.dismissedUntil = null;
	};

	const isDismissed = computed(() => {
		const until = consent.value.dismissedUntil;
		return typeof until === 'number' && until > Date.now();
	});

	const setAll = (value: boolean) => {
		ensureConsentShape();
		consent.value.functional = true;
		consent.value.analytics = value;
		consent.value.targeting = value;
	};

	const ensureFunctionalEnabled = () => {
		ensureConsentShape();
		consent.value.functional = true;
	};

	return {
		consent,
		byType,
		dismissFor,
		resetDismissal,
		isDismissed,
		setAll,
		ensureFunctionalEnabled,
	};
}
